import {
  IVacationAward,
  IVacationAwardingSlotUsage,
  IVacationBidSlot,
  IVacationChangeAward,
  IVacationChangeCrew,
  IVacationChangeRequest,
  IVacationChangeRequestDecisionResponse,
  IVacationChangeRequestsResponse,
  IVacationChangeWindowListItem,
  IVacationChangeWindowScheduleRow,
  IVacationChangeWindowSummary,
  IVacationDate,
  IVacationLeftoverPlacement,
  IVacationMonthDay,
  VacationAwardSource,
  VacationBidRound,
  VacationChangeRequestKind,
  VacationChangeRequestStatus,
  VacationChangeWindowState,
  blockUnderLeftoverDays,
  changeRequestKind,
  leftoverDayRange,
} from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";

import { crewName, slotLengthDays } from "../Awarding/awardingHelpers";
import { compareVacationDates } from "../helpers";

const { Draft, Upcoming, Open, Closed } = VacationChangeWindowState;

const plural = (count: number, one: string, many = `${one}s`): string => `${count} ${count === 1 ? one : many}`;

/** The blocks with a place left. */
export const openSlotIds = (usage: IVacationAwardingSlotUsage[]): Set<string> =>
  new Set(usage.filter((z) => z.remaining > 0).map((z) => z.slotId));

/**
 * The window being worked on: the one that has not closed, or has closed with requests still to
 * decide. Null when the next window can be started.
 */
export const currentWindow = (windows: IVacationChangeWindowListItem[]): IVacationChangeWindowListItem | null =>
  windows.find((z) => z.state !== Closed || z.pendingCount > 0) ?? null;

export interface IWindowEditable {
  /** The open date. */
  opens: boolean;
  /** The close date. */
  closes: boolean;
  /** The offered blocks. */
  slots: boolean;
  delete: boolean;
  share: boolean;
}

/** What can change about a window in its current state; null is a window not created yet. */
export const windowEditable = (window: Pick<IVacationChangeWindowListItem, "state"> | null): IWindowEditable => {
  switch (window?.state ?? null) {
    case null:
      return { opens: true, closes: true, slots: true, delete: false, share: false };
    case Draft:
      return { opens: true, closes: true, slots: true, delete: true, share: true };
    case Upcoming:
      return { opens: true, closes: true, slots: true, delete: true, share: false };
    case Open:
      return { opens: false, closes: true, slots: false, delete: false, share: false };
    default:
      return { opens: false, closes: false, slots: false, delete: false, share: false };
  }
};

/** A time in the package's base zone, or UTC without one. */
const atBase = (value: Date | string, timezoneId: string | undefined): moment.Moment =>
  timezoneId ? moment(value).tz(timezoneId) : moment.utc(value);

/** A time as the scheduler reads it: in the package's base zone, labelled with that zone. */
export const formatInstant = (value: Date | string, timezoneId: string | undefined): string =>
  atBase(value, timezoneId).format("MMM DD, YYYY HH:mm z");

/** A day as the scheduler reads it, in the package's base zone. */
export const formatDay = (value: Date | string | null | undefined, timezoneId: string | undefined): string =>
  value ? atBase(value, timezoneId).format("MMM DD") : "";

/** Where a window stands, in words. */
export const windowStateLabel = (
  window: Pick<IVacationChangeWindowListItem, "state" | "opensAt" | "closesAt" | "pendingCount">,
  timezoneId: string | undefined
): string => {
  switch (window.state) {
    case Draft:
      return "Draft - crew cannot see it until it is shared";
    case Upcoming:
      return `Shared - opens to crew ${formatInstant(window.opensAt, timezoneId)}`;
    case Open:
      return `Open to crew until ${formatInstant(window.closesAt, timezoneId)}`;
    default:
      return window.pendingCount
        ? `Closed - ${plural(window.pendingCount, "request")} to decide`
        : `Closed ${formatInstant(window.closesAt, timezoneId)}`;
  }
};

/** A window's open and close days, with their times when it opens and closes on the same day. */
export const windowDatesLabel = (
  window: Pick<IVacationChangeWindowListItem, "opensAt" | "closesAt">,
  timezoneId: string | undefined
): string => {
  const opens = atBase(window.opensAt, timezoneId);
  const closes = atBase(window.closesAt, timezoneId);
  return opens.isSame(closes, "day")
    ? `${opens.format("MMM DD, YYYY HH:mm")} - ${closes.format("HH:mm z")}`
    : `${opens.format("MMM DD, YYYY")} - ${closes.format("MMM DD, YYYY")}`;
};

/** A package list cell: where its change window stands, and how many requests wait on it. */
export const windowSummaryLabel = (summary: IVacationChangeWindowSummary | null | undefined): string => {
  if (!summary) return "";
  const pending = summary.pendingCount ? plural(summary.pendingCount, "request") : "";
  switch (summary.state) {
    case Draft:
      return "Draft";
    case Upcoming:
      return "Shared";
    case Open:
      return pending ? `Open · ${pending}` : "Open";
    default:
      return pending ? `${pending} to decide` : "Closed";
  }
};

const blockHasStarted = (slot: Pick<IVacationBidSlot, "startDate">, today: IVacationDate): boolean =>
  compareVacationDates(slot.startDate, today) <= 0;

/**
 * The blocks a window can offer: those starting after the day it closes at the base (or after today), and on or
 * after its `slotsFrom`.
 */
export const offerableSlotIds = (
  slots: IVacationBidSlot[],
  today: IVacationDate,
  window: { closesAt: string | null; slotsFrom: IVacationDate | null } | null,
  timezoneId: string | undefined
): Set<string> => {
  const close = window?.closesAt ? atBase(window.closesAt, timezoneId) : null;
  const lastDay = close ? { year: close.year(), month: close.month() + 1, day: close.date() } : today;
  const after = compareVacationDates(lastDay, today) > 0 ? lastDay : today;
  const from = window?.slotsFrom;
  return new Set(
    slots
      .filter((z) => !blockHasStarted(z, after) && (!from || compareVacationDates(z.startDate, from) >= 0))
      .map((z) => z.slotId)
  );
};

/** A close picked into an empty field lands on 00:00, so it moves to 23:59 that day at the base. */
export const closeAtEndOfDay = (previous: string | null, picked: string, timezoneId: string | undefined): string => {
  const at = atBase(picked, timezoneId);
  return previous || at.hours() || at.minutes() ? picked : at.set({ hour: 23, minute: 59 }).toISOString();
};

/** A month/day in a year. A day the month lacks that year (Feb 29) becomes its last. */
const inYear = (monthDay: IVacationMonthDay, year: number): IVacationDate => ({
  year,
  month: monthDay.month,
  day: Math.min(monthDay.day, moment.utc([year, monthDay.month - 1]).daysInMonth()),
});

const onOrAfter = (monthDay: IVacationMonthDay, from: IVacationDate): IVacationDate => {
  const sameYear = inYear(monthDay, from.year);
  return compareVacationDates(sameYear, from) >= 0 ? sameYear : inYear(monthDay, from.year + 1);
};

const baseInstant = (day: IVacationDate, hour: number, minute: number, timezoneId: string | undefined): string => {
  const wall = [day.year, day.month - 1, day.day, hour, minute];
  return (timezoneId ? moment.tz(wall, timezoneId) : moment.utc(wall)).toISOString();
};

/**
 * The window a customer's schedule sets next: the soonest whose close day has not passed. It opens at 00:00 and
 * closes at 23:59 at the base. Null without a schedule.
 */
export const scheduledWindow = (
  schedule: IVacationChangeWindowScheduleRow[] | null | undefined,
  today: IVacationDate,
  timezoneId: string | undefined
): { opensAt: string; closesAt: string; slotsFrom: IVacationDate } | null => {
  const next = (schedule ?? [])
    .flatMap((row) =>
      [today.year - 1, today.year, today.year + 1].map((year) => {
        const opens = inYear(row.opens, year);
        const closes = onOrAfter(row.closes, opens);
        return { opens, closes, slotsFrom: onOrAfter(row.slotsFrom, closes) };
      })
    )
    .filter((z) => compareVacationDates(z.closes, today) >= 0)
    .sort((a, b) => compareVacationDates(a.closes, b.closes))[0];
  if (!next) return null;
  return {
    opensAt: baseInstant(next.opens, 0, 0, timezoneId),
    closesAt: baseInstant(next.closes, 23, 59, timezoneId),
    slotsFrom: next.slotsFrom,
  };
};

export const crewById = (crew: IVacationChangeCrew[]): Map<string, IVacationChangeCrew> =>
  new Map(crew.map((z) => [z.userId, z]));

/** A crew member's name, from the view's crew, however little is known about them. */
export const changeCrewName = (crew: Map<string, IVacationChangeCrew>, userId: string): string => {
  const member = crew.get(userId);
  return member ? crewName(member) : "Unknown crew member";
};

/**
 * A crew member's whole blocks of entitlement not yet awarded, from the view as it now stands. Null
 * when their entitlement has no block count, or they are not in the view.
 */
export const unfilledBlocksOf = (
  view: Pick<IVacationChangeRequestsResponse, "awards" | "crew">,
  userId: string
): number | null => {
  const blocks = view.crew.find((z) => z.userId === userId)?.blocks;
  if (blocks === null || blocks === undefined) return null;
  return Math.max(0, blocks - view.awards.filter((z) => String(z.userId) === userId).length);
};

/** Fewer days than this left after whole blocks are leftover days, not a short block of their own. */
const MIN_SHORT_BLOCK_DAYS = 3;

/**
 * The days a swap adds to those of the block given up, as the server does: where blocks differ in length, the
 * entitlement's days that no award or leftover days cover. Elsewhere those are picked up.
 */
const swapExtraDays = (
  member: Pick<IVacationChangeCrew, "blocks" | "totalDays"> | undefined,
  held: Pick<IVacationChangeAward, "blockDays" | "leftoverDays">[]
): number => {
  if (!member || member.blocks !== null) return 0;
  const days = held.reduce((total, z) => total + z.blockDays + (z.leftoverDays?.days ?? 0), 0);
  return Math.max(0, member.totalDays - days);
};

/**
 * The days the block a request takes covers, or will once approved, against its slot's length. A swap passes on the
 * given-up block's days, adding unused days where blocks differ in length, and a pick-up gets the days still owed, never
 * more than the slot holds. An approved request gives the days it recorded. Null without a block to take, or once the
 * block given up has gone some other way.
 */
export const requestBlockDays = (
  view: Pick<IVacationChangeRequestsResponse, "awards" | "crew">,
  request: Pick<IVacationChangeRequest, "userId" | "releaseSlotId" | "takeSlotId" | "takeBlockDays">,
  slotsById: Map<string, Pick<IVacationBidSlot, "startDate" | "endDate">>
): { days: number; length: number } | null => {
  const take = request.takeSlotId ? slotsById.get(request.takeSlotId) : undefined;
  if (!take) return null;
  const length = slotLengthDays(take);
  if (request.takeBlockDays) return { days: request.takeBlockDays, length };
  const held = view.awards.filter((z) => String(z.userId) === String(request.userId));
  const taken = held.find((z) => z.slotId === request.takeSlotId);
  if (taken) return { days: taken.blockDays, length };
  const member = view.crew.find((z) => z.userId === String(request.userId));
  if (request.releaseSlotId) {
    const released = held.find((z) => z.slotId === request.releaseSlotId);
    return released ? { days: Math.min(released.blockDays + swapExtraDays(member, held), length), length } : null;
  }
  if (!member) return null;
  const owed = member.totalDays - (member.leftoverDays ?? 0) - held.reduce((total, z) => total + z.blockDays, 0);
  return { days: owed >= MIN_SHORT_BLOCK_DAYS && owed < length ? owed : length, length };
};

type IRequestShape = Pick<IVacationChangeRequest, "releaseSlotId" | "takeSlotId" | "leftoverPlacement">;

/**
 * Whether a pending request's change already landed, so approving it only records the decision. A request that only
 * places leftover days has landed once the days sit where it asked.
 */
export const requestApplied = (
  view: Pick<IVacationChangeRequestsResponse, "awards">,
  request: Pick<IVacationChangeRequest, "_id" | "status" | "userId"> & IRequestShape
): boolean => {
  if (request.status !== VacationChangeRequestStatus.Pending) return false;
  if (changeRequestKind(request) !== VacationChangeRequestKind.PlaceLeftover) {
    return view.awards.some((z) => !!z.changeRequestId && String(z.changeRequestId) === String(request._id));
  }
  const placement = request.leftoverPlacement;
  return view.awards.some(
    (z) =>
      String(z.userId) === String(request.userId) &&
      z.slotId === placement?.slotId &&
      z.leftoverDays?.side === placement?.side
  );
};

/** Where a request puts leftover days, for example `after JUL-12`. */
export const placementLabel = (
  placement: IVacationLeftoverPlacement,
  slotLabel: (slotId: string) => string = (slotId) => slotId
): string => `${placement.side} ${slotLabel(placement.slotId)}`;

/**
 * Why a request's leftover placement no longer stands, mirroring the server: required when the release carries the
 * days, refused once they are placed elsewhere, and only on a block held after the change that has not started.
 * Neither the days staying put nor the days being placed can land on another block held after it.
 */
const leftoverBlockedReason = (
  view: Pick<IVacationChangeRequestsResponse, "awards" | "crew" | "slots" | "today">,
  request: Pick<IVacationChangeRequest, "userId"> & IRequestShape,
  held: Set<string>,
  slotLabel: (slotId: string) => string
): string | null => {
  const userId = String(request.userId);
  const { releaseSlotId, takeSlotId, leftoverPlacement: placement } = request;
  const carrierAward = view.awards.find((z) => String(z.userId) === userId && z.leftoverDays);
  const carrier = carrierAward?.slotId ?? null;
  const releasesCarrier = !!releaseSlotId && releaseSlotId === carrier;
  const slotOf = (slotId: string): IVacationBidSlot | undefined => view.slots.find((z) => z.slotId === slotId);

  const carrierSlot = carrier ? slotOf(carrier) : undefined;
  const take = takeSlotId ? slotOf(takeSlotId) : undefined;
  if (!releasesCarrier && carrierSlot && carrierAward?.leftoverDays && take) {
    const { side, days } = carrierAward.leftoverDays;
    if (blockUnderLeftoverDays(leftoverDayRange(carrierSlot, side, days), [take])) {
      return `The leftover days ${side} ${slotLabel(carrierSlot.slotId)} fall on ${slotLabel(take.slotId)}`;
    }
  }

  if (!placement)
    return releasesCarrier ? `${slotLabel(releaseSlotId)} carries the leftover days, with nowhere named` : null;
  const leftoverDays = view.crew.find((z) => z.userId === userId)?.leftoverDays;
  if (!releasesCarrier) {
    if (carrier) return "The leftover days are already placed";
    if (leftoverDays === null) return "Leftover days cannot be counted in this package";
    if (!leftoverDays) return "No leftover days to place";
  }
  const heldAfter = new Set([...held].filter((z) => z !== releaseSlotId));
  if (takeSlotId) heldAfter.add(takeSlotId);
  if (!heldAfter.has(placement.slotId)) return `No longer holds ${slotLabel(placement.slotId)} for the leftover days`;
  const target = slotOf(placement.slotId);
  if (!target) return null;
  if (blockHasStarted(target, view.today)) return `${slotLabel(placement.slotId)} has already started`;
  const others = view.slots.filter((z) => z.slotId !== placement.slotId && heldAfter.has(z.slotId));
  const landsOn = leftoverDays
    ? blockUnderLeftoverDays(leftoverDayRange(target, placement.side, leftoverDays), others)
    : null;
  return landsOn
    ? `Leftover days ${placement.side} ${slotLabel(placement.slotId)} would fall on ${slotLabel(landsOn.slotId)}`
    : null;
};

/**
 * Why a pending request can no longer be approved, from the view as it now stands, or null while it
 * still can. Such a request can only be denied.
 */
export const approveBlockedReason = (
  view: Pick<IVacationChangeRequestsResponse, "awards" | "crew" | "slots" | "today">,
  request: Pick<IVacationChangeRequest, "_id" | "userId" | "status"> & IRequestShape,
  slotLabel: (slotId: string) => string = (slotId) => slotId
): string | null => {
  if (request.status !== VacationChangeRequestStatus.Pending || requestApplied(view, request)) return null;
  const userId = String(request.userId);
  const held = new Set(view.awards.filter((z) => String(z.userId) === userId).map((z) => z.slotId));
  const { releaseSlotId, takeSlotId } = request;
  const kind = changeRequestKind(request);

  if (kind === VacationChangeRequestKind.Swap && releaseSlotId && !held.has(releaseSlotId)) {
    return `No longer holds ${slotLabel(releaseSlotId)}, so this request can only be denied`;
  }
  if (kind === VacationChangeRequestKind.PickUp) {
    const unfilled = unfilledBlocksOf(view, userId);
    if (unfilled === null) return "Entitlement cannot be counted in blocks, so this request can only be denied";
    if (unfilled < 1) return "No longer short, so this request can only be denied";
  }
  if (takeSlotId) {
    if (held.has(takeSlotId)) return `Already holds ${slotLabel(takeSlotId)}, so this request can only be denied`;
    const take = view.slots.find((z) => z.slotId === takeSlotId);
    if (!take) return `${slotLabel(takeSlotId)} is no longer offered, so this request can only be denied`;
    const release = view.slots.find((z) => z.slotId === releaseSlotId);
    for (const slot of [release, take]) {
      if (slot && blockHasStarted(slot, view.today)) {
        return `${slotLabel(slot.slotId)} has already started, so this request can only be denied`;
      }
    }
  }
  const leftover = leftoverBlockedReason(view, request, held, slotLabel);
  return leftover ? `${leftover}, so this request can only be denied` : null;
};

/** Where a request sits among the same crew member's requests in the list; null when it is their only one. */
export const requestPosition = (
  requests: Pick<IVacationChangeRequest, "_id" | "userId">[],
  request: Pick<IVacationChangeRequest, "_id" | "userId">
): string | null => {
  const theirs = requests.filter((z) => String(z.userId) === String(request.userId));
  if (theirs.length < 2) return null;
  return `request ${theirs.findIndex((z) => z._id === request._id) + 1} of ${theirs.length}`;
};

/** Who holds each block, by name. */
export const holderNamesBySlot = (
  view: Pick<IVacationChangeRequestsResponse, "awards" | "crew">
): Map<string, string[]> => {
  const crew = crewById(view.crew);
  const names = new Map<string, string[]>();
  for (const award of view.awards) {
    names.set(award.slotId, [...(names.get(award.slotId) ?? []), changeCrewName(crew, String(award.userId))]);
  }
  return names;
};

export interface IBlockCount {
  awarded: number;
  capacity: number;
  /** The awarded count once the request is approved. */
  after: number;
}

export interface IRequestCounts {
  /** Null for a pick-up, which gives no block up. */
  release: IBlockCount | null;
  /** Null for a request that only places leftover days. */
  take:
    | (IBlockCount & {
        /** No place is left, so approving goes over capacity. */
        full: boolean;
      })
    | null;
}

/** Whether a request is a pick-up: it takes a block without giving one up. */
export const isPickUp = (request: Pick<IVacationChangeRequest, "releaseSlotId" | "takeSlotId">): boolean =>
  changeRequestKind(request) === VacationChangeRequestKind.PickUp;

/**
 * What approving a request does to its blocks' counts. A swap's two counts change together; a
 * pick-up only raises the block taken.
 */
export const requestCounts = (
  request: Pick<IVacationChangeRequest, "releaseSlotId" | "takeSlotId">,
  usageBySlot: Map<string, IVacationAwardingSlotUsage>
): IRequestCounts => {
  const release = request.releaseSlotId ? usageBySlot.get(request.releaseSlotId) : undefined;
  const take = request.takeSlotId ? usageBySlot.get(request.takeSlotId) : undefined;
  return {
    release: request.releaseSlotId
      ? {
          awarded: release?.awarded ?? 0,
          capacity: release?.capacity ?? 0,
          after: (release?.awarded ?? 0) - 1,
        }
      : null,
    take: request.takeSlotId
      ? {
          awarded: take?.awarded ?? 0,
          capacity: take?.capacity ?? 0,
          after: (take?.awarded ?? 0) + 1,
          full: (take?.remaining ?? 0) <= 0,
        }
      : null,
  };
};

/** Why a window's requests cannot be decided yet, or null once the server reports it closed. */
export const decisionsBlockedReason = (
  window: Pick<IVacationChangeWindowListItem, "state" | "closesAt"> | null | undefined,
  timezoneId: string | undefined
): string | null =>
  window?.state === Closed
    ? null
    : `Requests can be decided once the window closes${window ? ` on ${formatInstant(window.closesAt, timezoneId)}` : ""}.`;

export const REQUEST_STATUS_LABEL: Record<VacationChangeRequestStatus, string> = {
  [VacationChangeRequestStatus.Pending]: "Pending review",
  [VacationChangeRequestStatus.Approved]: "Approved",
  [VacationChangeRequestStatus.Denied]: "Denied",
  [VacationChangeRequestStatus.Withdrawn]: "Withdrawn by crew",
};

/**
 * The blocks a crew member was awarded in a round before any change request, found by undoing their approved changes,
 * newest first. A block taken through a change is never counted, even when the history to undo it is missing.
 */
export const awardedBeforeChanges = (
  held: Pick<IVacationAward, "slotId" | "source" | "round">[],
  requests: Pick<IVacationChangeRequest, "status" | "releaseSlotId" | "takeSlotId" | "resolvedAt">[],
  round: VacationBidRound
): Set<string> => {
  // A swap moves the award, round and all, so a block given up was awarded in the round of the one taken for it.
  const roundOf = new Map(held.map((z) => [z.slotId, z.round]));
  const awarded = new Set(held.filter((z) => z.source !== VacationAwardSource.ChangeRequest).map((z) => z.slotId));
  const approved = requests
    .filter((z) => z.status === VacationChangeRequestStatus.Approved)
    .sort((a, b) => new Date(b.resolvedAt ?? 0).getTime() - new Date(a.resolvedAt ?? 0).getTime());
  for (const request of approved) {
    if (request.takeSlotId) awarded.delete(request.takeSlotId);
    if (request.releaseSlotId) {
      awarded.add(request.releaseSlotId);
      roundOf.set(request.releaseSlotId, roundOf.get(request.takeSlotId ?? ""));
    }
  }
  return new Set([...awarded].filter((z) => roundOf.get(z) === round));
};

/** One crew member's requests, newest first, as their change history. */
export const requestHistoryOf = (requests: IVacationChangeRequest[], userId: string): IVacationChangeRequest[] =>
  requests
    .filter((z) => String(z.userId) === userId)
    .sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());

/** The requests made in one window, in the order the server gives them: most senior first. */
export const requestsInWindow = (
  requests: IVacationChangeRequest[],
  windowId: string | null | undefined
): IVacationChangeRequest[] => requests.filter((z) => String(z.changeWindowId) === windowId);

/** Folds a decision into the view; the response's awards replace all of that crew member's awards. */
export const applyDecisionResponse = (
  view: IVacationChangeRequestsResponse,
  response: IVacationChangeRequestDecisionResponse
): IVacationChangeRequestsResponse => {
  const { request } = response;
  const userId = String(request.userId);
  const previous = view.requests.find((z) => z._id === request._id);
  const resolvedNow =
    previous?.status === VacationChangeRequestStatus.Pending && request.status !== VacationChangeRequestStatus.Pending;

  return {
    ...view,
    usage: response.usage,
    awards: [...view.awards.filter((z) => String(z.userId) !== userId), ...response.awards],
    requests: view.requests.map((z) => (z._id === request._id ? request : z)),
    windows: view.windows.map((window) =>
      resolvedNow && window._id === String(request.changeWindowId)
        ? { ...window, pendingCount: Math.max(0, window.pendingCount - 1) }
        : window
    ),
  };
};
